import React, { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PhoneIcon } from "@heroicons/react/24/solid";
import { CheckCircleIcon, LockClosedIcon, MicrophoneIcon, UserIcon } from "@heroicons/react/24/outline";
import mixpanel from "mixpanel-browser";
import ReactGA from "react-ga4";
import { BcallLogo } from "./components/BcallLogo";
import { LanguageSelector, TranslationKey, translationCatalog, useLocale } from "./i18n";
import { API_BASE_URL, CallCredentials, normalizeCallSession, storeCallCredentials } from "./rtc/callSession";

mixpanel.init("f4710b9de9fbf50c638ae49451c3261f", { debug: false });
ReactGA.initialize("G-M6184ES3SR");

interface PublicProfile {
  name: string;
  displayName: string;
  headline: string;
  headlineCustomized: boolean;
  avatarUrl: string;
}

interface DialerInfoResponse {
  status?: string;
  name?: string;
  displayName?: string;
  headline?: string;
  headlineCustomized?: boolean;
  avatarUrl?: string;
  device?: string;
  country?: string;
  rtcBackend?: string;
  rtcAvailable?: boolean;
  error?: string;
}

function LoadingScreen() {
  const { dir, t } = useLocale();
  return (
    <main className="caller-page" dir={dir}>
      <LanguageSelector className="language-selector-floating" />
      <div className="caller-loading" role="status"><span className="caller-spinner" /><p>{t("loadingPage")}</p></div>
    </main>
  );
}

function ErrorScreen({ retry }: { retry: () => void }) {
  const { dir, t } = useLocale();
  return (
    <main className="caller-page" dir={dir}>
      <section className="caller-card caller-state-card">
        <LanguageSelector className="language-selector-state" />
        <div className="caller-state-icon error" aria-hidden="true">!</div>
        <h1>{t("notFoundTitle")}</h1>
        <p>{t("notFoundBody")}</p>
        <button type="button" className="caller-secondary-button" onClick={retry}>{t("retry")}</button>
        <BcallFooter />
      </section>
    </main>
  );
}

function ProfileAvatar({ profile, available }: { profile: PublicProfile; available: boolean }) {
  const { t } = useLocale();
  const initial = Array.from(profile.displayName.trim())[0]?.toUpperCase() || "B";
  return (
    <div className="caller-avatar">
      {profile.avatarUrl ? <img src={profile.avatarUrl} alt={t("avatarAlt", { name: profile.displayName })} /> : <span aria-hidden="true">{initial}</span>}
      <i className={`caller-online-dot${available ? "" : " offline"}`} aria-hidden="true" />
    </div>
  );
}

function BcallFooter() {
  const { t } = useLocale();
  return <a href="https://bcall.us/" className="caller-footer" aria-label="Bcall.us"><BcallLogo size={20} /><span>{t("poweredBy")}</span></a>;
}

function referrerSource(): string {
  if (!document.referrer) return "";
  try { return new URL(document.referrer).hostname.slice(0, 128); } catch { return ""; }
}

function callErrorKey(code?: string): TranslationKey {
  if (code === "EXTENSION_OFFLINE") return "errorExtensionOffline";
  if (code === "EXTENSION_UPGRADE_REQUIRED") return "errorReceiverUpgrade";
  if (code === "RATE_LIMITED") return "errorRateLimited";
  if (code === "RTC_MISCONFIGURED" || code === "SESSION_CREATE_FAILED") return "errorServiceUnavailable";
  return "errorStartGeneric";
}

export function resolvePublicSlug(pathname: string): string {
  const slug = pathname.replace(/^\/+|\/+$/g, "").toLowerCase();
  return /^[a-z0-9]{1,64}$/.test(slug) ? slug : "";
}

export function PublicPageNotFound() {
  return <ErrorScreen retry={() => window.location.reload()} />;
}

export default function App() {
  const navigate = useNavigate();
  const { dir, locale, source, t } = useLocale();
  const initialLocale = useRef({ locale, source });
  const currentLocale = useRef({ locale, source });
  currentLocale.current = { locale, source };
  const location = useLocation();
  const slug = resolvePublicSlug(location.pathname);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [profile, setProfile] = useState<PublicProfile>({ name: slug, displayName: slug, headline: "", headlineCustomized: false, avatarUrl: "" });
  const [callerName, setCallerName] = useState("");
  const [startingCall, setStartingCall] = useState(false);
  const [rtcBackend, setRtcBackend] = useState("kamailio");
  const [rtcAvailable, setRtcAvailable] = useState(true);
  const [callError, setCallError] = useState<TranslationKey | null>(null);

  const availabilityLabel = useMemo(() => t(rtcAvailable ? "available" : "unavailable"), [rtcAvailable, t]);
  const visibleHeadline = profile.headlineCustomized ? profile.headline : t("defaultHeadline");

  useEffect(() => {
    document.title = notFound ? t("notFoundTitle") : loading ? t("metaTitle") : t("profileTitle", { name: profile.displayName });
    document.querySelector('meta[name="description"]')?.setAttribute("content", t("metaDescription"));
  }, [loading, notFound, profile.displayName, t]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    if (!slug) {
      setLoading(false);
      setNotFound(true);
      return;
    }
    fetch(`${API_BASE_URL}/dialer-info?name=${encodeURIComponent(slug)}`)
      .then(async response => {
        const result = await response.json() as DialerInfoResponse;
        if (!response.ok || result.status === "404") throw Object.assign(new Error(result.error || "Not found"), { status: response.status });
        return result;
      })
      .then(result => {
        if (cancelled) return;
        const resolvedName = result.name || slug;
        // Match the API's public-text normalization when recognizing its legacy default.
        // eslint-disable-next-line no-control-regex
        const normalizedHeadline = (result.headline || "").replace(/[\u0000-\u001f\u007f]/g, " ").replace(/\s+/g, " ").trim();
        // Older signup APIs stored this generated text and incorrectly marked it as custom.
        const legacyDefault = normalizedHeadline === translationCatalog.he.defaultHeadline;
        setProfile({
          name: resolvedName,
          displayName: result.displayName || resolvedName,
          headline: result.headline || "",
          headlineCustomized: Boolean(normalizedHeadline && !legacyDefault && result.headlineCustomized !== false),
          avatarUrl: result.avatarUrl || ""
        });
        setRtcBackend(result.rtcBackend || "kamailio");
        setRtcAvailable(result.rtcAvailable !== false);
        setLoading(false);
        mixpanel.identify(resolvedName);
        mixpanel.track("Show dialer", { Country: result.country, Device: result.device, Locale: initialLocale.current.locale, LocaleSource: initialLocale.current.source });
        mixpanel.people.increment("Show_dialer_count", 1);
        sessionStorage.removeItem("rtcCallSession");
        sessionStorage.setItem("name", resolvedName);
      })
      .catch(error => {
        if (cancelled) return;
        setLoading(false);
        setNotFound(true);
        console.error("Unable to load public call profile", error);
      });
    return () => { cancelled = true; };
  }, [reloadKey, slug]);

  async function startCall(event: FormEvent) {
    event.preventDefault();
    if (startingCall || (rtcBackend === "kamailio" && !rtcAvailable)) return;
    setStartingCall(true);
    setCallError(null);
    const normalizedCallerName = callerName.trim().slice(0, 64);

    try {
      let credentials: CallCredentials;
      if (rtcBackend === "asterisk") {
        const response = await fetch(`${API_BASE_URL}/dialer?name=${encodeURIComponent(profile.name)}`);
        const result = await response.json();
        if (!response.ok || result.status === "404") throw Object.assign(new Error(result.error), { code: result.code });
        credentials = {
          user: result.userpass, authorizationUsername: result.userpass, pass: result.userpass, targetSipUser: result.numberadv,
          domain: process.env.REACT_APP_LEGACY_SIP_DOMAIN || "bcall.us",
          websocket: process.env.REACT_APP_LEGACY_SIP_WEBSOCKET || "wss://phone1.bcall.us:443/ws",
          apiBaseUrl: API_BASE_URL,
          iceServers: [{ urls: ["stun:turn.bcall.us:3478"] }, { urls: ["turn:turn.bcall.us:3478?transport=udp", "turn:turn.bcall.us:3478?transport=tcp"], username: result.turn.username, credential: result.turn.password }]
        };
      } else {
        const response = await fetch(`${API_BASE_URL}/call-sessions`, {
          method: "POST",
          headers: { "content-type": "application/json", "idempotency-key": window.crypto.randomUUID() },
          body: JSON.stringify({ name: profile.name, callerName: normalizedCallerName, referrer: referrerSource() })
        });
        const result = await response.json();
        if (!response.ok) throw Object.assign(new Error(result.error), { code: result.code });
        credentials = normalizeCallSession(result);
      }

      sessionStorage.setItem("callerName", normalizedCallerName);
      sessionStorage.setItem("callRecipientName", profile.displayName);
      sessionStorage.setItem("callReturnPath", `${window.location.pathname}${window.location.search}` || "/");
      storeCallCredentials(credentials);
      const selectedLocale = currentLocale.current;
      mixpanel.track("Click call dialer", { hasCallerName: Boolean(normalizedCallerName), Locale: selectedLocale.locale, LocaleSource: selectedLocale.source });
      mixpanel.people.increment("Show_click_call_count", 1);
      const diagnostics = new URLSearchParams(window.location.search).get("setupTrace") === "1" ? "&setupTrace=1" : "";
      navigate(`/dialer?lang=${selectedLocale.locale}${diagnostics}`);
    } catch (error) {
      console.error("Failed to create call session", error);
      setCallError(callErrorKey((error as Error & { code?: string }).code));
    } finally {
      setStartingCall(false);
    }
  }

  if (loading) return <LoadingScreen />;
  if (notFound) return <ErrorScreen retry={() => setReloadKey(value => value + 1)} />;

  return (
    <main className="caller-page" dir={dir}>
      <section className="caller-card" aria-labelledby="profile-name">
        <div className="caller-brand-row">
          <div className="caller-brand-copy"><span>{t("directCall")}</span><LanguageSelector /></div>
          <BcallLogo size={32} />
        </div>
        <div className="caller-profile">
          <ProfileAvatar profile={profile} available={rtcAvailable} />
          <div>
            <span className={`caller-availability ${rtcAvailable ? "available" : "unavailable"}`}><i aria-hidden="true" />{availabilityLabel}</span>
            <h1 id="profile-name" dir="auto">{profile.displayName}</h1>
            <p dir="auto">{visibleHeadline}</p>
          </div>
        </div>
        <div className="caller-trust-row" aria-label={t("callDetails")}>
          <span><CheckCircleIcon />{t("noInstall")}</span><span><LockClosedIcon />{t("privateNumber")}</span>
        </div>
        <form className="caller-form" onSubmit={startCall}>
          <label htmlFor="caller-name">
            <span>{t("yourName")} <small>{t("optional")}</small></span>
            <div className="caller-input-wrap"><UserIcon aria-hidden="true" /><input id="caller-name" type="text" value={callerName} onChange={event => { setCallerName(event.target.value); setCallError(null); }} placeholder={t("namePlaceholder")} maxLength={64} autoComplete="name" dir="auto" /></div>
          </label>
          {callError && <div className="caller-error" role="alert">{t(callError)}</div>}
          <button type="submit" className="caller-primary-button" disabled={startingCall || (rtcBackend === "kamailio" && !rtcAvailable)}>
            {startingCall ? <span className="caller-spinner small" /> : <PhoneIcon aria-hidden="true" />}
            <span>{startingCall ? t("startingCall") : rtcBackend === "kamailio" && !rtcAvailable ? t("unavailableButton") : t("startCall")}</span>
          </button>
          <p className="caller-microphone-note"><MicrophoneIcon aria-hidden="true" />{t("microphoneNote")}</p>
        </form>
        <BcallFooter />
      </section>
    </main>
  );
}
